import { createElement } from 'react';
import { isCrossOrigin, carryOrigin } from '@rad/hooks/useRouting';
import APPSETTINGS from '@appsettings';
import { obscureUrl, pathToURL } from './urls/_';
import { accountUrls, exploreUrls, propertyUrls, orgUrls } from './urls/general';
import { communityPathFor, withoutCommunityPath } from '../community-path';
export const getBaseURL = (subdomain = '') => {
    const url = new URL(APPSETTINGS.APP_HOST);
    if (subdomain)
        url.hostname = `${subdomain}.${url.hostname}`;
    return url.toString().replace(/\/+$/, '');
};
export const getStaticPageURL = (path) => `/s/${path}`;
// A URL this page built for its own app, on another host
const onHost = (subdomain, url) => getBaseURL(subdomain) + withoutCommunityPath(url);
// A URL in a community's app: on its own subdomain, or, for an organization without one, under
// /o/{subdomain} on the root host. `url` is a route URL as this page builds it.
export const getCommunityURL = (community, url) => {
    var _a;
    const subdomain = (_a = community.subdomain) !== null && _a !== void 0 ? _a : '';
    return acrossHosts(community.kind === 'root' ? onHost('', url)
        : community.kind === 'organization' && !community.subdomainEnabled ? getBaseURL() + communityPathFor(subdomain) + withoutCommunityPath(url)
            : onHost(subdomain, url));
};
// "root" resolves to an empty subdomain as the default
const districtSubdomain = (x) => x.communities.reduce((acc, c) => c.kind === 'district' ? c.subdomain : acc, '');
// A link to another host loses this page's history, so the page rides along in the URL for the far side's back link.
export const acrossHosts = (url) => isCrossOrigin(url) ? carryOrigin(url) : url;
export const getPropertyURL = (property) => acrossHosts(onHost(districtSubdomain(property), propertyUrls.property.getURL({ id: property.id })));
// The shared page for a parcel, on the root host: a district host would swap in its own view.
export const getSharedPropertyURL = (propertyId, suffix = '') => acrossHosts(onHost('', propertyUrls.property.getURL({ id: propertyId })) + suffix);
// The signed-in user's saved homes, which live on their account on the root host.
export const getSavedPropertiesURL = () => acrossHosts(onHost('', accountUrls.account.getURL({ tab: 'saved' })));
// Explore, which only the root host serves.
export const getExploreURL = () => acrossHosts(onHost('', exploreUrls.explore.getURL()));
// The page for a geocoded address with no parcel row yet. Nothing identifies it but the address
// and where it is, so both travel in the query string.
export const getAddressURL = (address, point) => `${propertyUrls.address.getURL()}?address=${encodeURIComponent(address)}&lon=${point[0]}&lat=${point[1]}`;
// The page for an address nobody has contributed to yet, keyed by parcel rather than property.
export const getParcelURL = (parcel) => acrossHosts(onHost(districtSubdomain(parcel), propertyUrls.parcel.getURL({ id: parcel.id })));
// A view's permanent address: a district's on its own host, an organization's on the root host wearing the organization's chrome.
export const getOrgStoryURL = (story) => {
    var _a;
    const subdomain = (_a = story.community.subdomain) !== null && _a !== void 0 ? _a : '';
    const host = story.community.kind === 'district' ? subdomain : '';
    return acrossHosts(onHost(host, orgUrls.story.getURL({ org: subdomain, id: story.id })));
};
// TODO: deprecated
export function getRootURL() {
    return getBaseURL();
    //return APPSETTINGS.APP_HOST
}
// TODO: deprecated
export function getDistrictURL(subdomain) {
    return getBaseURL(subdomain);
    //const url = new URL(APPSETTINGS.APP_HOST)
    //url.hostname = `${subdomain}.${url.hostname}`
    //return url.toString().replace(/\/+$/, '')
}
const obscureRoute = (url, component) => (Object.assign(Object.assign({}, obscureUrl(url)), { 'match': (ismatch) => {
        const params = ismatch(url.path);
        if (!params)
            return null;
        return createElement(component, params);
    } }));
// A route table: every URL of `urls`, in the same order, with the screen it shows
export const mkRoutes = (urls, screens) => {
    const keys = Object.keys(urls);
    return [
        Object.fromEntries(keys.map(key => [key, Object.assign(Object.assign({}, urls[key]), { 'component': screens[key] })])),
        keys.map(key => obscureRoute(urls[key], screens[key])),
    ];
};
// The hub a nav entry sits in for a community of this kind
export const navGroupFor = (display, kind) => kind === 'organization' && display.organizationGroup ? display.organizationGroup : display.group;
// Build flat navigation items from routes with optional filtering
export function buildNavItems(routes, options) {
    return routes
        .filter(route => {
        if (!route.nav)
            return false;
        if ((options === null || options === void 0 ? void 0 : options.group) && navGroupFor(route.nav.display, options.kind) !== options.group)
            return false;
        if ((options === null || options === void 0 ? void 0 : options.hasPermission) && route.permissions.length > 0 && !options.hasPermission(route.permissions))
            return false;
        return true;
    })
        .map(route => {
        const nav = route.nav;
        const { label, icon, description, section } = nav.display;
        return {
            key: route.path[route.path.length - 1],
            href: nav.url(),
            label, icon, description, section,
            route: route.path,
        };
    });
}
// Build navigation sections grouped by section field
export function buildNavSections(routes, options) {
    const items = buildNavItems(routes, options).filter(item => item.section);
    const grouped = Map.groupBy(items, item => item.section);
    return Array.from(grouped, ([title, items]) => ({ title, items }));
}
// Admin nav group definitions for top-level navigation
const adminNavGroups = [
    { key: 'properties', label: 'Properties', icon: 'bi-building', route: ['manage', 'properties'] },
    { key: 'community', label: 'Community', organizationLabel: 'Your Team', icon: 'bi-people', route: ['manage', 'community'] },
    { key: 'content', label: 'Content', icon: 'bi-file-text', route: ['manage', 'content'] },
];
// Label and icon of an admin hub as a community of this kind sees it
export function getAdminNavGroup(key, kind) {
    var _a;
    const group = (_a = adminNavGroups.find(g => g.key === key)) !== null && _a !== void 0 ? _a : adminNavGroups[0];
    const label = kind === 'organization' && group.organizationLabel ? group.organizationLabel : group.label;
    return { label, icon: group.icon };
}
// True when the user holds at least one permission that grants access to a
// non-hub admin route — i.e. there's actually something for them to manage.
// Empty-permission routes (hub redirects like `/manage/properties`) are
// excluded since those are reachable to everyone but show nothing useful
// without further permissions. Use this to gate UI affordances like the
// "Manage" menu item; hiding it for non-admins avoids leading them into a
// dead-end shell.
export function hasAnyAdminPermission(routeSets, hasPermission) {
    return routeSets.flat().some(r => r.permissions.length > 0 && hasPermission(r.permissions));
}
// The system area's landing page for this user — the first system screen they may use, on the root
// host — or null when they may use none. System permissions are platform-wide, so it lives in the
// user menu rather than beside any one community's admin hubs.
export function getSystemURL(systemRoutes, hasPermission) {
    const [first] = buildNavItems(systemRoutes, { hasPermission });
    return first ? acrossHosts(onHost('', first.href)) : null;
}
// Build admin navigation with 3 grouped hub items
export function buildAdminNavItems(routeSets, hasPermission, kind) {
    const routes = routeSets.flat();
    return adminNavGroups
        .filter(group => buildNavItems(routes, { group: group.key, kind, hasPermission }).length > 0)
        .map(({ key, route }) => (Object.assign(Object.assign({ key }, getAdminNavGroup(key, kind)), { href: pathToURL(route), route })));
}
